import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { buildPaginationTokens } from '../../../../utils/paginationTokens'
import { extractApiErrorMessage } from '../../../../utils/apiError'
import { listJobProjectOptions, listJobs, listJobTaskOptions, listJobUserOptions, listTimesheetsByJob } from '../api/jobApi'
import { toTaskOption, toProjectOption, buildSelectOptions, fetchOrgFilterOptions } from '../../common/pmOptionUtils'

const DEFAULT_PAGE_SIZE = 10
const MIN_DATE = '1900-01-01'
const MAX_DATE = '9999-12-31'

function normalizeStatus(value) {
  const normalized = String(value || 'DRAFT').toUpperCase()
  if (normalized === 'IN_PROGESS') return 'IN_PROGRESS'
  if (['DRAFT', 'IN_PROGRESS', 'PENDING_REVIEW', 'COMPLETED', 'CLOSED'].includes(normalized)) return normalized
  return 'DRAFT'
}

function statusLabel(status) {
  if (status === 'IN_PROGRESS') return 'In Progress'
  if (status === 'PENDING_REVIEW') return 'Pending Review'
  if (status === 'COMPLETED') return 'Completed'
  if (status === 'CLOSED') return 'Closed'
  return 'Draft'
}

function toDisplayDate(value) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit' })
}

function computeProgress(status, isCompleted, id) {
  if (isCompleted || status === 'COMPLETED') return 100
  if (status === 'CLOSED') return 95
  if (status === 'DRAFT') return 4
  return 30 + (Number(id || 1) % 55)
}

export function useJobListScreen() {
  const [searchParams, setSearchParams] = useSearchParams()

  const searchText = searchParams.get('search') || ''
  const projectFilter = searchParams.get('project_id') || 'All'
  const taskFilter = searchParams.get('task_id') || 'All'
  const statusFilter = searchParams.get('status') || 'All'
  const responsibleFilter = searchParams.get('responsible_id') || 'All'
  const companyFilter = searchParams.get('company_id') || 'All'
  const branchFilter = searchParams.get('branch_id') || 'All'
  const departmentFilter = searchParams.get('department_id') || 'All'
  const overrunFilter = searchParams.get('overrun') || 'All'
  const fromDate = searchParams.get('fromDate') || ''
  const toDate = searchParams.get('toDate') || ''
  const page = Number(searchParams.get('page') || 1)

  const [pageSize] = useState(DEFAULT_PAGE_SIZE)
  const [items, setItems] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [loggedHoursByJob, setLoggedHoursByJob] = useState({})
  const [projectOptions, setProjectOptions] = useState([])
  const [taskOptions, setTaskOptions] = useState([{ value: 'All', label: 'All Tasks' }])
  const [userOptions, setUserOptions] = useState([])
  const [companyOptions, setCompanyOptions] = useState([{ value: 'All', label: 'All Companies' }])
  const [branchOptions, setBranchOptions] = useState([{ value: 'All', label: 'All Branches' }])
  const [departmentOptions, setDepartmentOptions] = useState([{ value: 'All', label: 'All Departments' }])
  const [meta, setMeta] = useState({
    total: 0,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    hasMore: false,
  })

  const queryParams = useMemo(
    () => ({
      page,
      page_size: pageSize,
      includeTotal: true,
      archive: false,
      search: searchText.trim(),
      fromDate: fromDate || MIN_DATE,
      toDate: toDate || MAX_DATE,
      ...(projectFilter !== 'all' && projectFilter !== 'All' ? { project_id: Number(projectFilter) } : {}),
      ...(taskFilter !== 'all' && taskFilter !== 'All' ? { task_id: Number(taskFilter) } : {}),
      ...(statusFilter !== 'all' && statusFilter !== 'All' ? { status: statusFilter } : {}),
      ...(responsibleFilter !== 'all' && responsibleFilter !== 'All' ? { assignedTo: Number(responsibleFilter) } : {}),
      ...(companyFilter !== 'All' ? { company_id: Number(companyFilter) } : {}),
      ...(branchFilter !== 'All' ? { branch_id: Number(branchFilter) } : {}),
      ...(departmentFilter !== 'All' ? { department_id: Number(departmentFilter) } : {}),
      ...(overrunFilter !== 'All' ? { overrun: overrunFilter } : {}),
    }),
    [
      fromDate,
      page,
      pageSize,
      projectFilter,
      taskFilter,
      responsibleFilter,
      searchText,
      statusFilter,
      toDate,
      companyFilter,
      branchFilter,
      departmentFilter,
      overrunFilter,
    ],
  )

  useEffect(() => {
    let active = true
    async function loadMasterOptions() {
      try {
        const [projectsRes, usersRes, orgOptions] = await Promise.allSettled([
          listJobProjectOptions({
            page: 1,
            page_size: 100,
            includeTotal: true,
            archive: false,
            fromDate: MIN_DATE,
            toDate: MAX_DATE,
          }),
          listJobUserOptions({ page: 1, page_size: 200 }),
          fetchOrgFilterOptions({ pageSize: 200 }),
        ])

        if (!active) return

        if (projectsRes.status === 'fulfilled') {
          setProjectOptions(Array.isArray(projectsRes.value) ? projectsRes.value : [])
        }
        if (usersRes.status === 'fulfilled') {
          setUserOptions(Array.isArray(usersRes.value) ? usersRes.value : [])
        }
        if (orgOptions.status === 'fulfilled') {
          setCompanyOptions(orgOptions.value.companyOptions)
          setBranchOptions(orgOptions.value.branchOptions)
          setDepartmentOptions(orgOptions.value.departmentOptions)
        }
      } catch {
        // silent fallback
      }
    }
    loadMasterOptions()
    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    let active = true
    async function loadTasks() {
      try {
        const params = {
          page_size: 100,
          archive: false,
          fromDate: MIN_DATE,
          toDate: MAX_DATE,
        }
        if (projectFilter !== 'all' && projectFilter !== 'All') {
          params.project_id = Number(projectFilter)
        }
        const rawTasks = await listJobTaskOptions(params)
        if (!active) return
        setTaskOptions(buildSelectOptions(rawTasks, toTaskOption, 'All Tasks'))
      } catch {
        if (!active) return
        setTaskOptions([{ value: 'All', label: 'All Tasks' }])
      }
    }
    loadTasks()
    return () => {
      active = false
    }
  }, [projectFilter])

  useEffect(() => {
    let active = true

    async function loadRows() {
      setIsLoading(true)
      setError('')
      try {
        const response = await listJobs(queryParams)
        if (!active) return

        setItems(Array.isArray(response?.items) ? response.items : [])
        setMeta({
          total: Number(response?.total || 0),
          page: Number(response?.page || page),
          pageSize: Number(response?.pageSize || pageSize),
          hasMore: Boolean(response?.hasMore),
        })
      } catch (requestError) {
        if (!active) return
        setError(extractApiErrorMessage(requestError, 'Failed to load jobs.'))
      } finally {
        if (active) setIsLoading(false)
      }
    }

    loadRows()
    return () => {
      active = false
    }
  }, [page, pageSize, queryParams])

  useEffect(() => {
    let active = true
    async function hydrateLoggedHours() {
      if (!items.length) {
        setLoggedHoursByJob({})
        return
      }
      try {
        const pairs = await Promise.all(
          items.map(async (job) => {
            const rows = await listTimesheetsByJob(job.id)
            const total = rows.reduce((sum, row) => sum + Number(row?.hoursLogged || 0), 0)
            return [Number(job.id), Number(total.toFixed(2))]
          }),
        )
        if (!active) return
        setLoggedHoursByJob(Object.fromEntries(pairs))
      } catch {
        if (!active) return
        setLoggedHoursByJob({})
      }
    }
    hydrateLoggedHours()
    return () => {
      active = false
    }
  }, [items])



  const projectNameById = useMemo(() => {
    const map = new Map()
    projectOptions.forEach((project) => {
      map.set(Number(project.id), project.name || `Project #${project.id}`)
    })
    return map
  }, [projectOptions])

  const userById = useMemo(() => {
    const map = new Map()
    userOptions.forEach((user) => {
      map.set(Number(user.id), user)
    })
    return map
  }, [userOptions])

  const rows = useMemo(() => {
    return items
      .map((item) => {
        const status = normalizeStatus(item.status)
        const plannedHours = Number(item.planned_hours || 0)
        const loggedHours = Number(loggedHoursByJob[Number(item.id)] || 0)
        const progress =
          plannedHours > 0 ? Math.min(100, Math.round((loggedHours / plannedHours) * 100)) : computeProgress(status, Number(item.is_completed || 0) === 1, item.id)
        const assignedUser = userById.get(Number(item.assigned_to))
        return {
          id: Number(item.id),
          jobCode: item.job_code || '-',
          jobName: item.job_name || '-',
          taskName: item.task_name || '-',
          projectId: Number(item.project_id || 0),
          projectName: projectNameById.get(Number(item.project_id || 0)) || item.project_name || `Project #${item.project_id || '-'}`,
          assignedUser,
          status,
          statusLabel: statusLabel(status),
          timelineText: `${toDisplayDate(item.planned_start)} - ${toDisplayDate(item.planned_end)}`,
          progress,
          loggedHours,
          plannedHours,
          overrunFlag: item.overrun_flag || null,  // 'RED' | 'GREEN' | null (SRS Feature 2 Phase 3)
          hasDateChanges: Boolean(item.hasDateChanges || (item.date_change_count && Number(item.date_change_count) > 0)),
        }
      })
  }, [items, loggedHoursByJob, projectNameById, userById])

  const totalPages = useMemo(() => {
    if (!meta.total || !meta.pageSize) return meta.hasMore ? page + 1 : Math.max(1, page)
    return Math.max(1, Math.ceil(meta.total / meta.pageSize))
  }, [meta.hasMore, meta.pageSize, meta.total, page])

  const rangeStart = useMemo(() => {
    if (!rows.length) return 0
    return (page - 1) * pageSize + 1
  }, [page, pageSize, rows.length])

  const rangeEnd = useMemo(() => {
    if (!rows.length) return 0
    return rangeStart + rows.length - 1
  }, [rangeStart, rows.length])

  const stats = useMemo(() => {
    const inProgress = rows.filter((row) => row.status === 'IN_PROGRESS').length
    const avgUtilization = rows.length ? Math.round(rows.reduce((sum, row) => sum + row.progress, 0) / rows.length) : 0
    const completed = rows.filter((row) => row.status === 'COMPLETED').length
    return {
      inProgress,
      avgUtilization,
      completed,
    }
  }, [rows])

  function updateParam(key, value, resetPage = true) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      const cleanValue = key === 'search' ? String(value || '') : String(value || '').trim()
      
      if (cleanValue && cleanValue !== 'all') {
        next.set(key, cleanValue)
      } else {
        next.delete(key)
      }
      
      if (resetPage) {
        next.delete('page')
      }
      return next
    }, { replace: true })
  }

  function goToPage(nextPage) {
    if (nextPage < 1 || nextPage > totalPages) return
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (nextPage === 1) {
        next.delete('page')
      } else {
        next.set('page', String(nextPage))
      }
      return next
    }, { replace: true })
  }

  function goToPreviousPage() {
    goToPage(Math.max(1, page - 1))
  }

  function goToNextPage() {
    if (meta.hasMore || page < totalPages) goToPage(page + 1)
  }

  const formattedProjectOptions = useMemo(() => {
    return buildSelectOptions(projectOptions, toProjectOption, 'All Projects')
  }, [projectOptions])

  const formattedUserOptions = useMemo(() => {
    return [
      { value: 'All', label: 'All Assignees' },
      ...userOptions.map((u) => ({ value: String(u.id), label: u.name || u.email || `User ${u.id}` })),
    ]
  }, [userOptions])

  const searchField = searchParams.get('search_field') || 'Name'

  return {
    state: {
      searchField,
      searchText,
      projectFilter,
      taskFilter,
      statusFilter,
      responsibleFilter,
      companyFilter,
      branchFilter,
      departmentFilter,
      overrunFilter,
      fromDate,
      toDate,
      page,
      totalPages,
      totalCount: meta.total,
      hasMore: meta.hasMore,
      isLoading,
      error,
      rows,
      rangeStart,
      rangeEnd,
      searchFieldOptions: [
        { value: 'Name', label: 'Name' },
        { value: 'Code', label: 'Code' },
        { value: 'All', label: 'All' },
      ],
      projectOptions: formattedProjectOptions,
      taskOptions,
      userOptions: formattedUserOptions,
      companyOptions,
      branchOptions,
      departmentOptions,
      overrunOptions: [
        { value: 'All', label: 'All Efficiency' },
        { value: 'RED', label: 'Red Overrun (>120%)' },
        { value: 'GREEN', label: 'Green Efficient (<100%)' },
      ],
      statusOptions: [
        { value: 'All', label: 'All Statuses' },
        { value: 'DRAFT', label: 'Draft' },
        { value: 'IN_PROGRESS', label: 'In Progress' },
        { value: 'PENDING_REVIEW', label: 'Pending Review' },
        { value: 'COMPLETED', label: 'Completed' },
        { value: 'CLOSED', label: 'Closed' },
      ],
      pageTokens: buildPaginationTokens(page, totalPages),
      stats,
    },
    actions: {
      setSearchField: (value) => updateParam('search_field', value),
      setSearchText: (value) => updateParam('search', value),
      setProjectFilter: (value) => {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev)
          const cleanValue = String(value || '').trim()
          if (cleanValue && cleanValue !== 'all' && cleanValue !== 'All') {
            next.set('project_id', cleanValue)
          } else {
            next.delete('project_id')
          }
          next.delete('task_id')
          next.delete('page')
          return next
        }, { replace: true })
      },
      setTaskFilter: (value) => updateParam('task_id', value),
      setStatusFilter: (value) => updateParam('status', value),
      setResponsibleFilter: (value) => updateParam('responsible_id', value),
      setCompanyFilter: (value) => updateParam('company_id', value),
      setBranchFilter: (value) => updateParam('branch_id', value),
      setDepartmentFilter: (value) => updateParam('department_id', value),
      setOverrunFilter: (value) => updateParam('overrun', value),
      setFromDate: (value) => updateParam('fromDate', value),
      setToDate: (value) => updateParam('toDate', value),
      onReset: () => setSearchParams(new URLSearchParams(), { replace: true }),
      goToPage,
      goToPreviousPage,
      goToNextPage,
    },
  }
}
